// The energy page's two "look further" cards:
//   DayExplorer  — one day, every reading (power / current / voltage per phase)
//   BillHistory  — every bill period since the sensor started, its units and ₹
//                  on the formula, and the REAL bill beside it (the bill check)

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, LinearProgress, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getCurrentSenseRows } from '../../../core/api/api';
import {
  addDays, analyse, compareBill, monthName, periodMonths, periodsBetween, ymd,
} from '../../../core/devices/energyAnalysis';
import { loadDays, readPeriodTotal, writePeriodTotal } from '../../../core/devices/energyData';
import { billFor } from '../../../core/devices/energyTariff';
import { Section } from './EnergySections';
import {
  PHASE, dateRange, kwh, rupees,
} from './energyFormat';

// ── One day in full ────────────────────────────────────────────────────────

const METRICS = {
  power: { label: 'Power', unit: 'W', key: 'power' },
  current: { label: 'Current', unit: 'A', key: 'current' },
  voltage: { label: 'Voltage', unit: 'V', key: 'voltage' },
};

export function DayExplorer({ uno, phases }) {
  const theme = useTheme();
  const [day, setDay] = useState('');
  const [metric, setMetric] = useState('power');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async (d) => {
    setDay(d);
    if (!d) return;
    setBusy(true);
    setErr('');
    try {
      const res = await getCurrentSenseRows(uno, `${d} 00:00:00`, `${d} 23:59:59`);
      if (!Array.isArray(res.data)) throw new Error('bad reply');
      setRows(res.data);
    } catch (e) {
      setRows(null);
      setErr('Could not load that day.');
    } finally {
      setBusy(false);
    }
  };

  // One point per timestamp, thinned to ~360 so a phone draws it smoothly.
  const data = React.useMemo(() => {
    if (!rows) return [];
    const m = new Map();
    rows.forEach((r) => {
      const k = String(r.DateTime).slice(11, 19);
      if (!m.has(k)) m.set(k, { t: k.slice(0, 5) });
      m.get(k)[`p${Number(r.Phase ?? r.phase) || 1}`] = Number(r[METRICS[metric].key]) || 0;
    });
    const all = [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map((e) => e[1]);
    const step = Math.ceil(all.length / 360) || 1;
    return all.filter((_, i) => i % step === 0);
  }, [rows, metric]);

  return (
    <Section title="One day in detail">
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <TextField
          size="small"
          type="date"
          label="Day"
          value={day}
          onChange={(e) => load(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: ymd(new Date()) }}
        />
        {Object.entries(METRICS).map(([k, m]) => (
          <Chip key={k} size="small" label={m.label} color={metric === k ? 'primary' : 'default'} onClick={() => setMetric(k)} />
        ))}
        {busy && <CircularProgress size={18} />}
      </Box>
      {err && <Typography variant="body2" color="error">{err}</Typography>}
      {!day && <Typography variant="body2" color="text.secondary">Pick a day to see every reading (about 2 MB of data for a three-phase day).</Typography>}
      {rows && !rows.length && <Typography variant="body2" color="text.secondary">No readings on this day.</Typography>}
      {data.length > 0 && (
        <ResponsiveContainer width="100%" height={230}>
          <LineChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -6 }}>
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="t" tick={{ fill: theme.palette.text.secondary, fontSize: 10 }} stroke={theme.palette.divider} minTickGap={36} />
            <YAxis
              tick={{ fill: theme.palette.text.secondary, fontSize: 10 }}
              stroke={theme.palette.divider}
              width={48}
              domain={metric === 'voltage' ? ['dataMin - 5', 'dataMax + 5'] : [0, 'auto']}
              tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v))}
            />
            <Tooltip
              contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => [`${v} ${METRICS[metric].unit}`, n]}
            />
            {phases.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {phases.map((p) => (
              <Line key={p} type="monotone" dataKey={`p${p}`} name={PHASE[p].name} stroke={PHASE[p].color} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Section>
  );
}

// ── Every bill period, and the bill check ──────────────────────────────────

const MAX_PERIODS = 24;

export function BillHistory({
  uno, phases, settings, onSaveBills, onPickPeriod,
}) {
  const [rows, setRows] = useState(() => new Map()); // start ymd -> { recorded, estimated, coveragePct }
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ month: '', units: '', amount: '', estimated: false });
  const cancel = useRef(false);

  const now = new Date();
  const periods = periodsBetween(addDays(now, -MAX_PERIODS * 31), now, settings.cycleDay).slice(1, MAX_PERIODS + 1);

  // Cached totals for periods that have ended — shown without any download.
  useEffect(() => {
    const m = new Map();
    periods.forEach((p) => {
      const hit = readPeriodTotal(uno, `${settings.cycleDay}.${ymd(p.start)}`);
      if (hit) m.set(ymd(p.start), hit);
    });
    setRows(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno, settings.cycleDay]);

  useEffect(() => () => { cancel.current = true; }, []);

  const loadAll = async () => {
    setBusy(true);
    setErr('');
    cancel.current = false;
    const got = new Map(rows);
    const loaded = [];
    let emptyRun = 0;
    try {
      for (let i = 0; i < periods.length && !cancel.current; i += 1) {
        const p = periods[i];
        const key = ymd(p.start);
        setProgress(`${monthName(p.label)} (${i + 1} of up to ${periods.length})`);
        let total = got.get(key);
        if (!total) {
          // eslint-disable-next-line no-await-in-loop
          const { days } = await loadDays(uno, p.start, p.end);
          loaded.push({ p, days });
          total = null;
          const any = days.some((d) => d.hours.length);
          if (!any) {
            emptyRun += 1;
            got.set(key, { recorded: 0, estimated: 0, coveragePct: 0, empty: true });
            setRows(new Map(got));
            if (emptyRun >= 2) break;
            continue; // eslint-disable-line no-continue
          }
        }
        emptyRun = total && total.empty ? emptyRun + 1 : 0;
        if (emptyRun >= 2) break;
      }

      // Totals now that the first day with readings is known (the oldest
      // period must not "estimate" the days before the sensor was installed).
      const firstData = loaded
        .flatMap((x) => x.days.filter((d) => d.hours.length).map((d) => d.d))
        .sort()[0] || null;
      loaded.forEach(({ p, days }) => {
        if (!days.some((d) => d.hours.length)) return;
        const r = analyse(days, settings, { from: p.start, to: p.end, now, firstData, phases });
        const total = { recorded: r.recordedKwh, estimated: r.estimatedKwh, coveragePct: r.coveragePct };
        got.set(ymd(p.start), total);
        writePeriodTotal(uno, `${settings.cycleDay}.${ymd(p.start)}`, total);
      });
      setRows(new Map(got));
    } catch (e) {
      setErr('Could not load the history — check the connection and try again. What loaded is kept.');
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const billBy = new Map(settings.bills.map((b) => [b.month, b]));
  const shown = periods.filter((p) => rows.has(ymd(p.start)) || billBy.has(p.label));

  const addBill = () => {
    const units = Number(form.units);
    if (!form.month || !(units >= 0)) return;
    const next = settings.bills.filter((b) => b.month !== form.month);
    next.push({
      month: form.month, units, amount: form.amount === '' ? null : Number(form.amount), estimated: form.estimated,
    });
    onSaveBills(next);
    setForm({ month: '', units: '', amount: '', estimated: false });
  };

  return (
    <Section
      title="History and bill check"
      right={(
        <Button size="small" variant="outlined" onClick={busy ? () => { cancel.current = true; } : loadAll}>
          {busy ? 'Stop' : rows.size ? 'Refresh history' : 'Load history'}
        </Button>
      )}
    >
      {busy && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">{`Loading ${progress}…`}</Typography>
        </Box>
      )}
      {err && <Alert severity="warning" sx={{ mb: 1 }}>{err}</Alert>}
      {!shown.length && !busy && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Load history to see every bill period since the sensor started, priced on the formula above.
          Add your real bills below and each period shows how far the sensor is from the bill.
        </Typography>
      )}

      {shown.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.75, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Bill</TableCell>
                <TableCell align="right">Sensor</TableCell>
                <TableCell align="right">₹ formula</TableCell>
                <TableCell align="right">Bill units</TableCell>
                <TableCell align="right">Bill ₹</TableCell>
                <TableCell align="right">Difference</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((p) => {
                const t = rows.get(ymd(p.start));
                const units = t ? t.recorded + t.estimated : null;
                const bill = billBy.get(p.label);
                const cmp = bill && units ? compareBill(bill, units, settings.tariff, settings.sanctionedKw) : null;
                const priced = units != null
                  ? billFor(units, settings.tariff, { sanctionedKw: settings.sanctionedKw, months: periodMonths(p) }).total : null;
                return (
                  <TableRow
                    key={p.label}
                    hover
                    onClick={() => t && !t.empty && onPickPeriod(p)}
                    sx={{ cursor: t && !t.empty ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      {monthName(p.label)}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{dateRange(p.start, p.end)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {t ? (t.empty ? 'no readings' : kwh(units, 0)) : '—'}
                      {t && !t.empty && t.coveragePct < 97 && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>{`${t.coveragePct}% covered`}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{priced != null && !t?.empty ? rupees(priced) : '—'}</TableCell>
                    <TableCell align="right">
                      {bill ? bill.units : '—'}
                      {bill && bill.estimated && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>average</Typography>}
                    </TableCell>
                    <TableCell align="right">{bill && bill.amount ? rupees(bill.amount) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: cmp && Math.abs(cmp.diffPct) > 10 ? 'warning.main' : undefined }}>
                      {cmp && cmp.diffPct != null ? `${cmp.diffPct > 0 ? '+' : ''}${cmp.diffPct.toFixed(0)}%` : '—'}
                    </TableCell>
                    <TableCell padding="none">
                      {bill && (
                        <IconButton
                          size="small"
                          aria-label="Remove bill"
                          onClick={(e) => { e.stopPropagation(); onSaveBills(settings.bills.filter((b) => b.month !== p.label)); }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Difference = sensor against the bill. A bill marked "average" was not read from the meter, so a big difference there is the bill's, not the sensor's. Tap a period to see it in the cards above.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
        <TextField
          select
          size="small"
          label="Add a bill for"
          value={form.month}
          onChange={(e) => setForm({ ...form, month: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          {[periodsBetween(now, now, settings.cycleDay)[0], ...periods].map((p) => (
            <MenuItem key={p.label} value={p.label}>{monthName(p.label)}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" type="number" label="Units" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} sx={{ width: 110 }} />
        <TextField size="small" type="number" label="Amount ₹ (optional)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} sx={{ width: 170 }} />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.estimated} onChange={(e) => setForm({ ...form, estimated: e.target.checked })} />}
          label={<Typography variant="body2">Average (not read)</Typography>}
        />
        <Button variant="contained" size="small" onClick={addBill} disabled={!form.month || form.units === ''}>Add bill</Button>
      </Box>
    </Section>
  );
}
